/**
 * Inputs for the newer kinds of form question: protected numbers (Social Security, EIN, bank
 * routing and account numbers), entered hidden with a "Show" switch and checked as they're typed,
 * and lists (each W-2, each dependent), one card per entry with "Add another".
 */
import { fillYear, parseDollars, validRouting, validTaxpayerId, type GroupField, type GroupRow, type Question, type Scalar } from "@clientflow/shared";
import { Eye, EyeOff, Plus, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LENGTH: Record<string, [number, number]> = { ssn: [9, 9], ein: [9, 9], routing: [9, 9], account: [4, 17] };
const PLACEHOLDER: Record<string, string> = { ssn: "123-45-6789", ein: "12-3456789", routing: "9-digit routing number", account: "Account number" };

/** Why a protected number isn't right yet, or null. */
export function sensitiveProblem(type: string, text: string): string | null {
  const d = text.replace(/[\s-]/g, "");
  if (!d) return null;
  if (!/^\d+$/.test(d)) return "Enter numbers only.";
  const [min, max] = LENGTH[type] ?? [1, 30];
  if (d.length < min || d.length > max) return min === max ? `Enter all ${min} digits.` : `Enter ${min} to ${max} digits.`;
  if (type === "ssn" && !validTaxpayerId(d)) return "This is not a valid Social Security number or ITIN. Please check the number.";
  if (type === "routing" && !validRouting(d)) return "This routing number is not valid. It is the nine-digit number at the bottom left of a check.";
  return null;
}

export function SensitiveInput({ id, type, value, onChange, onInvalid, required }: { id: string; type: string; value: string | undefined; onChange: (v: string | undefined) => void; onInvalid: (bad: boolean) => void; required?: boolean }) {
  const [shown, setShown] = useState(false);
  const [text, setText] = useState(value ?? "");
  const problem = sensitiveProblem(type, text);
  return (
    <div className="max-w-xs space-y-1">
      <div className="flex gap-1">
        <Input
          id={id}
          type={shown ? "text" : "password"}
          inputMode="numeric"
          // "new-password" keeps browsers and password managers from offering to save the number.
          autoComplete="new-password"
          data-1p-ignore
          data-lpignore="true"
          placeholder={PLACEHOLDER[type]}
          value={text}
          aria-required={required || undefined}
          aria-invalid={!!problem || undefined}
          aria-describedby={problem ? `${id}-problem` : undefined}
          onChange={(e) => {
            const t = e.target.value;
            setText(t);
            const bad = sensitiveProblem(type, t) !== null;
            onInvalid(bad);
            if (!bad) onChange(t.replace(/[\s-]/g, "") || undefined);
          }}
        />
        <Button type="button" size="icon" variant="ghost" aria-label={shown ? "Hide number" : "Show number"} onClick={() => setShown((s) => !s)}>
          {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </Button>
      </div>
      {problem && (
        <p id={`${id}-problem`} className="text-xs text-destructive">
          {problem}
        </p>
      )}
    </div>
  );
}

/** One entry's field: the same kinds of answer as a question, plus protected numbers. */
function FieldInput({ id, f, value, onChange, onInvalid }: { id: string; f: GroupField; value: Scalar | undefined; onChange: (v: Scalar | undefined) => void; onInvalid: (bad: boolean) => void }) {
  const [money, setMoney] = useState(typeof value === "number" && f.type === "money" ? (value / 100).toFixed(2) : "");
  switch (f.type) {
    case "yes_no":
      return (
        <div className="flex gap-2" role="radiogroup" aria-labelledby={`${id}-label`}>
          {[true, false].map((v) => (
            <Button key={String(v)} type="button" role="radio" aria-checked={value === v} variant={value === v ? "default" : "outline"} size="sm" onClick={() => onChange(v)}>
              {v ? "Yes" : "No"}
            </Button>
          ))}
        </div>
      );
    case "choice":
      return (
        <select id={id} value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value || undefined)} className="block h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
          <option value="">Select an option</option>
          {(f.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    case "number":
      return <Input id={id} type="number" inputMode="decimal" value={typeof value === "number" ? value : ""} onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))} />;
    case "money":
      return (
        <Input
          id={id}
          inputMode="decimal"
          placeholder="$0.00"
          value={money}
          onChange={(e) => {
            setMoney(e.target.value);
            const t = e.target.value.trim();
            if (!t) return onInvalid(false), onChange(undefined);
            try {
              const c = parseDollars(t);
              onInvalid(false);
              onChange(c);
            } catch {
              onInvalid(true);
            }
          }}
        />
      );
    case "date":
      return <Input id={id} type="date" value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value || undefined)} />;
    case "ssn":
    case "ein":
    case "routing":
    case "account":
      return <SensitiveInput id={id} type={f.type} required={f.required} value={typeof value === "string" ? value : undefined} onChange={onChange} onInvalid={onInvalid} />;
    default:
      return <Input id={id} value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)} />;
  }
}

/** A list question: one card per entry, with "Add another". */
export function GroupInput({ q, rows, onChange, onInvalid, year }: { q: Question; rows: GroupRow[]; year?: number; onChange: (rows: GroupRow[]) => void; onInvalid: (bad: boolean) => void }) {
  const item = q.itemLabel ?? "item";
  // "Add a dependent", "Add an asset", "Add a W-2": form names keep their capitals mid-sentence.
  const inline = /\d/.test(item) || /^[A-Z]{2}/.test(item) ? item : item.charAt(0).toLowerCase() + item.slice(1);
  const article = /^[aeiou]/i.test(inline) ? "an" : "a";
  const max = q.maxRows ?? 20;
  const list = rows;
  // Each entry keeps its own id, so removing one from the middle never shows its numbers on the
  // next entry, and a half-typed number in a removed entry doesn't keep the form from saving.
  const nextId = useRef(0);
  const ids = useRef<number[]>([]);
  while (ids.current.length < list.length) ids.current.push(nextId.current++);
  if (ids.current.length > list.length) ids.current = ids.current.slice(0, list.length);
  const bad = useRef(new Set<string>());
  const mark = (k: string, isBad: boolean) => {
    const had = bad.current.has(k);
    if (isBad === had) return;
    if (isBad) bad.current.add(k);
    else bad.current.delete(k);
    onInvalid(bad.current.size > 0);
  };
  const remove = (i: number) => {
    const gone = `${ids.current[i]}.`;
    for (const k of [...bad.current]) if (k.startsWith(gone)) bad.current.delete(k);
    ids.current = ids.current.filter((_, j) => j !== i);
    onInvalid(bad.current.size > 0);
    onChange(list.filter((_, j) => j !== i));
  };
  return (
    <div className="space-y-3">
      {list.map((row, i) => (
        <div key={ids.current[i]} className="rounded-lg border border-border p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-medium">
              {item} {i + 1}
              {q.titleField && row[q.titleField] ? <span className="font-normal text-muted-foreground"> · {String(row[q.titleField])}</span> : null}
            </p>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              aria-label={`Remove ${item} ${i + 1}`}
              onClick={() => remove(i)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {(q.fields ?? []).map((f) => {
              const id = `q-${q.key}-${ids.current[i]}-${f.key}`;
              return (
                <div key={f.key} className="min-w-0 space-y-1">
                  <Label htmlFor={id} id={`${id}-label`} className="text-xs">
                    {year ? fillYear(f.label, year) : f.label}
                    {f.required && <span className="text-destructive"> *</span>}
                  </Label>
                  <FieldInput
                    id={id}
                    f={f}
                    value={row[f.key]}
                    onInvalid={(b) => mark(`${ids.current[i]}.${f.key}`, b)}
                    onChange={(v) => {
                      const next = { ...row };
                      if (v === undefined || v === "") delete next[f.key];
                      else next[f.key] = v;
                      onChange(list.map((r, j) => (j === i ? next : r)));
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {list.length < max && (
        <Button type="button" size="sm" variant="outline" onClick={() => onChange([...list, {}])}>
          <Plus className="mr-1 h-4 w-4" /> {list.length ? `Add another ${inline}` : `Add ${article} ${inline}`}
        </Button>
      )}
      {list.length === 0 && <p className="text-xs text-muted-foreground">If none apply, leave this blank.</p>}
    </div>
  );
}
